import axios from "axios";
import Liferay from "./Liferay";

/**
 * Inicializa una instancia de Axios pre-configurada con valores por defecto.
 *
 * @returns Instancia de Axios.
 */
function getJsonwsAxiosInstance() {

  const instance = axios.create();

  instance.defaults.baseURL = "/api/jsonws";

  instance.defaults.params = {
    p_auth: Liferay.authToken,
  };

  return instance;

}

/**
 * Inicializa una instancia de Axios pre-configurada con valores por defecto.
 *
 * @returns Instancia de Axios.
 */
function getAxiosInstance() {

  const instance = axios.create();

  instance.defaults.baseURL = "/o";

  instance.defaults.headers.common.Accept = "application/json";
  instance.defaults.headers.common["x-csrf-token"] = Liferay.authToken;
  instance.defaults.headers.post["Content-Type"] = "application/json";
  instance.defaults.headers["Accept-Language"] = Liferay.ThemeDisplay.getBCP47LanguageId();

  return instance;

}

/**
 * Método que obtiene un doctor dado su id.
 *
 * @param doctorId - Id del doctor a buscar.
 *
 * @returns Doctor buscado.
 */
async function getDoctor(doctorId) {

  const nestedFields = "drNowDoctor,agendaConfigurations,offices,medicalAppointments,"
    .concat("medicalAppointmentDetails,bankAccounts,agendaConfigurations.doctorsOffice");

  return getAxiosInstance().get(`/c/doctors/${doctorId}`, {
    params: {
      nestedFields,
      nestedFieldsDepth: 2,
    },
  });

}

/**
 * Método que obtiene un usuario a partir de un id.
 *
 * @returns Resultado de la consulta.
 */
async function geMyLiferayUser() {

  return getAxiosInstance().get(
    `/headless-admin-user/v1.0/user-accounts/${Liferay.ThemeDisplay.getUserId()}`,
  );

}

/**
 * Método que obtiene un usuario a partir de un id.
 *
 * @returns Resultado de la consulta.
 */
async function geMyDrNowUser() {

  const userId = Liferay.ThemeDisplay.getUserId();

  const url = `/headless-admin-user/v1.0/user-accounts/${userId}/drNowUser`;
  const restrictFields = "actions,creator,keywords,status,taxonomyCategoryBriefs";

  return getAxiosInstance().get(url, {
    params: {
      restrictFields,
    },
  });

}

/**
 * Método que ejecuta el follow (seguir) a un doctor de parte del usuario actual.
 *
 * @param userId - Id del usuario a seguir.
 *
 * @returns Resultado del follow.
 */
async function followDoctor(userId) {

  return getAxiosInstance().post(`/dr-now-social-interactions/v1.0/users/${userId}/followers`);

}

/**
 * Método que ejecuta el unfollow (dejar de seguir) a un doctor de parte del usuario actual.
 *
 * @param userId - Id del usuario a dejar de seguir.
 *
 * @returns Resultado del unfollow.
 */
async function unfollowDoctor(userId) {

  return getAxiosInstance().delete(`dr-now-social-interactions/v1.0/my-user/following/${userId}`);

}

/**
 * Evalúa si el doctor es seguido por el usuario con sesión activa.
 *
 * @param doctorId - Id del doctor a evaluar.
 *
 * @returns Respuesta del servicio.
 */
async function doctorIsFollowed(doctorId) {

  return getAxiosInstance().get(`dr-now-social-interactions/v1.0/my-user/following/${doctorId}`);

}

/**
 * Obtiene los catálogos iniciales del componente.
 *
 * @returns Catálogo de países, tipos de identificación, géneros y areas de interes.
 */
async function getCountries() {

  return getAxiosInstance().get("/headless-admin-address/v1.0/countries", {
    params: {
      sort: "name",
      pageSize: 250,
    },
  });

}

/**
 * Obtiene los catálogos para la vista de agendar cita.
 *
 * @param {string} "erc" - Código de referencia para buscar la lista.
 *
 * @returns Catalogos para la vista de agendar cita.
 */
async function getPicklist(erc) {

  return getAxiosInstance().get(
    `/headless-admin-list-type/v1.0/list-type-definitions/by-external-reference-code/${erc}`,
  );

}

/**
 * Crea una cita de parte del usuario.
 *
 * @param appointmentData - Datos de la cita.
 *
 * @returns Respuesta del servicio.
 */
async function createAppointment(appointmentData) {

  return getAxiosInstance().post("c/medicalappointments/", appointmentData);

}

/**
 * Obtiene los recursos del calendario.
 *
 * @param doctorId - Id del usuario (doctor) del cual obtener el calendario.
 *
 * @returns Calendarios y citas.
 */
async function getCalendarsFromApi(doctorId) {

  const resp = await getAxiosInstance().get("c/medicalappointments/", {
    params: {
      filter: `r_doctorAppointments_c_doctorId eq '${doctorId}'`,
      pageSize: 100,
    },
  });

  return resp;

}

/**
 * Obtiene el time zone del usuario actual.
 *
 * @returns Time zone del usuario actual.
 */
async function getUserTimeZone() {

  const response = await (getJsonwsAxiosInstance()
    .get("user/get-current-user")).then((z) => z.data.timeZoneId);

  return response;

}

/**
 * Obtiene el time zone de un usuario específico.
 *
 * @param userId - Id del usuario a buscar.
 *
 * @returns Time zone del usuario en cuestión.
 */
async function getAnyUserTimeZone(userId) {

  const response = await (getJsonwsAxiosInstance()
    .get(`user/get-user-by-id/user-id/${userId}`)).then((z) => z.data.timeZoneId);

  return response;

}

const client = {
  getDoctor,
  followDoctor,
  unfollowDoctor,
  geMyLiferayUser,
  geMyDrNowUser,
  doctorIsFollowed,
  getCountries,
  getPicklist,
  createAppointment,
  getCalendarsFromApi,
  getUserTimeZone,
  getAnyUserTimeZone,
};

export default client;
